import * as React from "react";
import {
  KuikaListTableUserCell,
  type KuikaListTableColumn
} from "../../components/ui-kit/kuika-list-table/kuika-list-table";
import { KMainFunctions } from "./main-functions";
import {
  initialsFromName,
  lookupModification,
  type IRecentlyModifiedEntry,
  type ModificationIndex
} from "./recently-modified";

/**
 * The "Last modified" column, identical on every designer landing: who last changed the asset
 * and when, in one cell drawn the way the "Created" column draws its author — the writer's
 * avatar, the relative time beside it, the name on the avatar's tooltip.
 *
 * It reads the entity-modification feed rather than the row, because the eight list endpoints
 * disagree about which timestamps they carry and half of them carry none. An asset the feed does
 * not mention — because the fetch failed, or has not landed yet — renders an em dash. It never
 * falls back to a date from somewhere else, which would silently mix two different meanings of
 * "modified" in one column.
 */
export const buildLastModifiedColumn = <T,>(
  index: ModificationIndex,
  getId: (row: T) => string | undefined
): KuikaListTableColumn<T> => ({
  id: "lastModified",
  header: () => "Last modified",
  width: 180,
  // Undated rows sort last in both directions rather than jumping to the top.
  accessorFn: (row: T) => lookupModification(index, getId(row))?.modifiedAt ?? 0,
  cell: (row: T) => {
    const entry: IRecentlyModifiedEntry | undefined = lookupModification(index, getId(row));
    // A background save has no attributable writer: its time still shows, beside the
    // unknown-person glyph rather than a guessed name.
    const name: string | undefined = entry?.modifiedByName || undefined;
    return (
      <KuikaListTableUserCell
        caption={entry ? KMainFunctions.formatRelativeTime(new Date(entry.modifiedAt)) : undefined}
        name={name}
        photoUrl={entry?.modifiedByPhotoUrl || undefined}
        initials={initialsFromName(name) || undefined}
        seed={name ?? null}
      />
    );
  }
});
